import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { createWorkspace, getUserWorkspaces, setDefaultWorkspace } from './workspaceService';

/**
 * Check if user needs migration to workspace structure
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<boolean>} - True if migration needed
 */
export const needsMigration = async (userId) => {
  try {
    const workspaces = await getUserWorkspaces(userId);
    return workspaces.length === 0;
  } catch (error) {
    console.error('Error checking migration status:', error);
    return false;
  }
};

/**
 * Migrate user's templates to a workspace
 * Adds workspaceId and isPublic fields to existing templates
 * @param {string} userId - Firebase Auth UID
 * @param {string} workspaceId - Target workspace ID
 * @returns {Promise<number>} - Count of migrated templates
 */
export const migrateUserTemplates = async (userId, workspaceId) => {
  try {
    const templatesRef = collection(db, 'templates');
    const q = query(templatesRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);

    let migratedCount = 0;
    const batch = writeBatch(db);

    snapshot.docs.forEach(doc => {
      const data = doc.data();

      if (!data.workspaceId) {
        batch.update(doc.ref, {
          workspaceId: workspaceId,
          isPublic: false,
          updatedAt: serverTimestamp()
        });
        migratedCount++;
      }
    });

    if (migratedCount > 0) {
      await batch.commit();
      console.log(`Batch updated ${migratedCount} templates`);
    }

    return migratedCount;
  } catch (error) {
    console.error('Error migrating user templates:', error);
    throw error;
  }
};

/**
 * Migrate existing user to workspace structure
 * Creates default workspace from user's brand data and migrates templates
 * @param {string} userId - Firebase Auth UID
 * @param {object} userProfile - User profile document
 * @returns {Promise<string>} - Default workspace ID
 */
export const migrateUserToWorkspaces = async (userId, userProfile) => {
  try {
    const existingWorkspaces = await getUserWorkspaces(userId);
    if (existingWorkspaces.length > 0) {
      console.log('User already migrated, skipping');
      return existingWorkspaces[0].id;
    }

    const workspaceId = await createWorkspace(userId, {
      name: userProfile.brandName || 'My Brand',
      logoUrl: userProfile.brandLogoUrl || null,
      colors: userProfile.brandColors || {
        primary: '#9333ea',
        secondary: '#ec4899'
      }
    });

    console.log(`Created default workspace: ${workspaceId}`);

    const migratedCount = await migrateUserTemplates(userId, workspaceId);
    console.log(`Migrated ${migratedCount} templates to workspace ${workspaceId}`);

    await setDefaultWorkspace(userId, workspaceId);

    return workspaceId;
  } catch (error) {
    console.error('Error migrating user to workspaces:', error);
    throw error;
  }
};
